import { del, get, post, put } from './requests.js'
import Cookies from '_lib/cookies'

const CUSTOM_AUDIENCES = 'custom-audiences'
const AUDIENCE_CREATOR = 'audience-creator'
const AUDIENCES = 'audiences'
const DMP_AUDIENCES = 'dmp-audiences'
const REPORT = 'report'

export const getCustomAudience = (id) => get([CUSTOM_AUDIENCES, id])

export const getCustomAudiences = (params) =>
  get([CUSTOM_AUDIENCES], { removeTrailingSlash: true }, params)

export const updateCustomAudience = (id, payload) => put([CUSTOM_AUDIENCES, id], {}, payload)

export const deleteCustomAudience = (id) => put([CUSTOM_AUDIENCES, id], {}, { status: 'deleted' })

export const createCustomAudience = (id, payload) => post([CUSTOM_AUDIENCES, id], {}, payload)

export const getCustomAudienceSchema = () => get([CUSTOM_AUDIENCES, 'schema'])

export const getCustomAudienceEnum = (field) =>
  get([CUSTOM_AUDIENCES, 'enum', field], { removeTrailingSlash: true })

// The report is CSV, so it goes around the API client (which always parses JSON).
// `start` and `end` are optional, independent, inclusive UTC dates (YYYY-MM-DD).
export const getPdpLiveRampReport = ({ start, end } = {}) => {
  const params = new URLSearchParams()
  if (start) params.append('start', start)
  if (end) params.append('end', end)
  const query = params.toString()

  return fetch(
    `${window._api.backend}/${CUSTOM_AUDIENCES}/pdp-liveramp-report.csv${query ? `?${query}` : ''}`,
    {
      method: 'GET',
      headers: {
        Accept: 'text/csv',
        Authorization: `Bearer ${window._api.token}`,
      },
    }
  ).then((response) => {
    if (!response.ok) {
      throw new Error(`Report request failed with status ${response.status}`)
    }
    return response.text()
  })
}

export const getTaxonomyAudience = (id) => get([AUDIENCES], {}, { id })

export const createTaxonomyAudience = ({ name, taxonomy }) =>
  post([AUDIENCES, 'taxonomy'], { headers: { dmp_token: Cookies.getCookie('dmp_token') } }, { name, taxonomy })

export const updateTaxonomyAudience = (id, audience) =>
  put([AUDIENCES, `taxonomy?id=${id}`], { removeTrailingSlash: true, headers: { dmp_token: Cookies.getCookie('dmp_token') } }, audience)

export const searchTopic = (payload) => post([AUDIENCE_CREATOR, '/search'], {}, payload)
export const rescale = (payload) => post([AUDIENCE_CREATOR, '/rescale'], {}, payload)

export const searchSignal = (payload) => post([DMP_AUDIENCES, '/signals/search'], {}, payload)

export const getSizeEstimation = (payload) =>
  post([AUDIENCE_CREATOR, '/size-estimation'], {}, payload)

export const getRegionSizeEstimation = (payload) =>
  post([AUDIENCE_CREATOR, '/region-size-estimation'], {}, payload)

export const getTopicsSizes = (payload) =>
  post(
    [AUDIENCE_CREATOR, '/topics-size-estimation'],
    { transformPayload: false, removeTrailingSlash: true },
    payload
  )

export const search = (payload) => post(['searches'], {}, payload)

export const getContentRules = (id) => get(['content-rules', id])

// TODO: used by Search.jsx page who is not using payload as camel case
export const getAudiencesSearch = (params) =>
  get([AUDIENCES, 'search'], { transformPayload: false }, params)

// TODO: used by Search.jsx page who is not using payload as camel case
export const getCustomInsightsReport = (params) =>
  get([REPORT, 'custom-insights'], { transformPayload: false }, params)

// TODO: used by Search.jsx and AudienceInsightsReportPage page who is not using payload as camel case
export const getAllLastUpdatedReports = () =>
  get([REPORT, 'all-last-updated'], { transformPayload: false })

// TODO: used by Search.jsx page who is not using payload as camel case
export const getUserLastUpdatedReports = (params) =>
  get([REPORT, 'user-last-updated'], { transformPayload: false }, params)

export const mergeAudiences = ({
  ids = [],
  name = '',
  regions = [],
  destinations = [],
  status = 'draft',
}) => post(['custom-audiences', 'merge'], {}, { ids, name, regions, destinations, status })

// Links audience creator audiences to new conversations in a PDP organization.
// The env goes on the url (as the other predactiv admin calls do): datasphere-api
// reads it from the query string and the body only carries ids (max 50) and the org.
export const moveAudiencesToPdp = (ids, organizationId, env) =>
  post(
    [CUSTOM_AUDIENCES, `move-to-pdp?env=${env}`],
    { removeTrailingSlash: true },
    { ids, organizationId }
  )

export const getKeywordsToTopics = ({ keywords, regions, destinations }) =>
  post([AUDIENCE_CREATOR, 'keywords-to-topics'], {}, { keywords, regions, destinations })

export const getKeywordsToSignals = ({ keywords, exclude }) =>
  post([DMP_AUDIENCES, 'keywords-to-signals'], {}, { keywords, exclude })

export const createDmpAudience = (payload) => post([DMP_AUDIENCES], {}, payload)

export const updateDmpAudience = (payload) => put([DMP_AUDIENCES], {}, payload)

export const expandTopics = (payload) => get([AUDIENCE_CREATOR, 'expand-topic'], {}, payload)

export const getOfflineTopics = () => get([AUDIENCE_CREATOR, 'search/offline'])

export const postCustomAudience = (payload) => post(['custom-audiences'], {}, payload)

export const getExpiringAudiences = ({ fields }) =>
  get(
    [CUSTOM_AUDIENCES, 'expiring'],
    { transformPayload: false, removeTrailingSlash: true },
    { fields }
  )

export const getLookalikeAudienceSeeds = (id) =>
  get(['lookalike-audience-seeds', ...(id ? [id] : [])], {})

export const getLookalikeAudienceSeedsWithFilters = ({ email, query, skip = 0 }) =>
  get(
    [`lookalike-audience-seeds`],
    { removeTrailingSlash: true },
    {
      ...(email && { 'created_by.email': email }),
      ...(query && { q: query }),
      sort: 'updated_at,-1',
      status: 'processed',
      limit: 10,
      skip,
    }
  )

export const updateLookalikeAudienceSeeds = (id, payload) =>
  put(['lookalike-audience-seeds', ...(id ? [id] : [])], {}, payload)

export const deleteLookalikeAudienceSeed = (id) =>
  del(['lookalike-audience-seeds', ...(id ? [id] : [])], {})

export const searchLookalikeAudienceBrands = (payload) =>
  post(['lookalike-audience-seeds', 'search', 'brands'], { transformPayload: false }, payload)

export const getLookalikeAudienceSeedsSchema = () =>
  get(['lookalike-audience-seeds', 'schema'], { transformPayload: false })

export const getLookalikeAudienceSeedsEnum = () =>
  get([`/lookalike-audience-seeds/enum/created_by.email`])

export const cloneLookalikeAudienceSeeds = (payload) =>
  post(['lookalike-audience-seeds', 'clone', payload._id], { removeTrailingSlash: true }, payload)
